import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Row, RowData } from '@tanstack/react-table';
import { TableCell, TableRow } from '../Table';
import { cn } from '../../../../lib/utils';

interface ExpandableRowProps<TData extends RowData> {
  row: Row<TData>;
  renderContent: () => React.ReactNode;
  duration?: number;
  className?: string;
}

type Phase = 'collapsed' | 'expanding' | 'expanded' | 'collapsing';

export function ExpandableRow<TData>({
  row,
  renderContent,
  duration = 300,
  className,
}: ExpandableRowProps<TData>) {
  const expanded = row.getIsExpanded();
  const ref = useRef<HTMLDivElement>(null);
  const [prev, setPrev] = useState(expanded);
  const [phase, setPhase] = useState<Phase>(
    expanded ? 'expanded' : 'collapsed',
  );
  const isCollapsed = phase === 'collapsed';
  const isExpanding = phase === 'expanding';
  const isExpanded = phase === 'expanded';
  const isCollapsing = phase === 'collapsing';

  if (expanded !== prev) {
    setPrev(expanded);

    if (expanded && (isCollapsed || isCollapsing)) {
      setPhase('expanding');
    }
    if (!expanded && (isExpanded || isExpanding)) {
      setPhase('collapsing');
    }
  }

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (isExpanding) {
      element.style.height = element.scrollHeight + 'px';
      const timer = setTimeout(() => setPhase('expanded'), duration);
      return () => clearTimeout(timer);
    }

    if (isCollapsing) {
      element.style.height = element.scrollHeight + 'px';
      requestAnimationFrame(() => {
        element.style.height = '0px';
      });
      const timer = setTimeout(() => setPhase('collapsed'), duration);
      return () => clearTimeout(timer);
    }

    if (isExpanded) {
      element.style.height = 'auto';
    }

    if (isCollapsed) {
      element.style.height = '0px';
    }
  }, [duration, isCollapsed, isCollapsing, isExpanded, isExpanding]);

  if (isCollapsed) {
    return null;
  }

  return (
    <TableRow>
      <TableCell colSpan={row.getAllCells().length} className='eye:p-0'>
        <div
          className={cn('eye:text-wrap', className)}
          ref={ref}
          style={{
            height: isExpanding ? '0px' : 'auto',
            overflow: 'hidden',
            transition:
              isExpanding || isCollapsing
                ? `height ${duration}ms ease-in-out`
                : 'none',
          }}
        >
          <div>{renderContent()}</div>
        </div>
      </TableCell>
    </TableRow>
  );
}
